const formatDateTime = (isoString) => {
  if (!isoString) {
    return '';
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(isoString));
  } catch (error) {
    return isoString;
  }
};

const buildQuestionMap = (questions = {}) => {
  return Object.values(questions).reduce((map, question) => {
    if (question?.questionId) {
      map[question.questionId] = question;
    }
    return map;
  }, {});
};

const resolveRadioValue = (questionMap, questionId, value) => {
  if (!value) {
    return '';
  }

  const otherText = value.otherText && value.otherText.trim();
  if (otherText) {
    return otherText;
  }

  const selectedValue = value.selectedOptionValue;
  if (!selectedValue) {
    return '';
  }

  const optionLabel = questionMap[questionId]?.options?.find((option) => option.value === selectedValue)?.label;
  return optionLabel || selectedValue;
};

const resolveCheckboxValue = (questionMap, questionId, value) => {
  if (!value) {
    return '';
  }

  const otherText = value.otherText && value.otherText.trim();
  if (otherText) {
    return otherText;
  }

  const selectedValues = value.selectedOptionValues || [];
  if (!selectedValues.length) {
    return '';
  }

  const labels = selectedValues.map((val) => {
    const label = questionMap[questionId]?.options?.find((option) => option.value === val)?.label;
    return label || val;
  }).filter(Boolean);

  return labels.join(', ');
};

const capitalizeWords = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
};

const formatScheduleDate = (isoString) => {
  if (!isoString) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
    }).format(new Date(isoString));
  } catch (error) {
    return '';
  }
};

const formatScheduleTime = (isoString) => {
  if (!isoString) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeStyle: 'short',
    }).format(new Date(isoString));
  } catch (error) {
    return '';
  }
};

const resolveLegacyScheduleEvent = (event, index) => {
  const label = event.label && event.label.trim() ? capitalizeWords(event.label) : `Event ${index + 1}`;
  const days = event.repeats.length ? event.repeats.join(', ') : 'No repeat days';

  const startTime = formatScheduleTime(event.startTime);
  const endTime = formatScheduleTime(event.endTime);
  const times = [startTime, endTime].filter(Boolean).join(' - ') || 'No time set';

  const startDate = formatScheduleDate(event.startDate);
  const endDate = formatScheduleDate(event.endDate);
  const dates = [startDate, endDate || 'Ongoing'].filter(Boolean).join(' - ') || 'No date set';

  return {
    scheduleType: 'event',
    label,
    days,
    times,
    dates,
  };
};

const resolveFlexibleScheduleEvent = (event) => {
  const preferredDays = event.preferredDays.length ? event.preferredDays.join(', ') : 'No preferred days';
  const preferredTimes = event.preferredTimes.length ? event.preferredTimes.join(', ') : 'No preferred times';
  const visitDuration = event.visitDuration && event.visitDuration.trim() ? event.visitDuration.trim() : 'No visit duration';

  return {
    scheduleType: 'flexible',
    label: 'Flexible',
    preferredDays,
    preferredTimes,
    visitDuration,
  };
};

const resolveOncallScheduleEvent = (event) => {
  const visitLength = event.visitLength && event.visitLength.trim() ? event.visitLength.trim() : 'No visit length';

  return {
    scheduleType: 'oncall',
    label: 'On Call',
    visitLength,
  };
};

const resolveScheduleEvent = (event, index) => {
  if ('visitLength' in event) {
    return resolveOncallScheduleEvent(event);
  }

  if (
    'preferredDays' in event ||
    'preferredTimes' in event ||
    'visitDuration' in event
  ) {
    return resolveFlexibleScheduleEvent(event);
  }

  return resolveLegacyScheduleEvent(event, index);
};

const resolveScheduleValue = (value) => {
  return value.events.map((event, index) => resolveScheduleEvent(event, index));
};

const getAnswerDisplay = (answer, questionMap) => {
  if (!answer) {
    return '';
  }

  const {fieldType, value, questionId} = answer;

  if (fieldType === 'text') {
    const text = value?.text;
    return text && text.trim() ? text.trim() : '';
  }

  if (fieldType === 'radio') {
    return resolveRadioValue(questionMap, questionId, value);
  }

  if (fieldType === 'checkbox') {
    return resolveCheckboxValue(questionMap, questionId, value);
  }

  if (fieldType === 'schedule') {
    return resolveScheduleValue(value);
  }

  if (Array.isArray(value)) {
    return value.length ? value : '';
  }

  return typeof value === 'string' ? value : '';
};

const getAnsweredQuestions = (form, questionMap) => {
  const orderedQuestions = Object.values(form?.formDefinitionSnapshot?.questions || {}).sort((a, b) => {
    const rankA = typeof a.rank === 'number' ? a.rank : Number.MAX_SAFE_INTEGER;
    const rankB = typeof b.rank === 'number' ? b.rank : Number.MAX_SAFE_INTEGER;
    return rankA - rankB;
  });

  return orderedQuestions.map((question) => {
    const answer = form?.answers?.[question.questionId];
    const displayValue = getAnswerDisplay(answer, questionMap);

    const hasDisplayValue = Array.isArray(displayValue) ? displayValue.length > 0 : Boolean(displayValue);

    if (!hasDisplayValue) {
      return null;
    }

    return {
      question,
      displayValue,
      fieldType: answer?.fieldType,
    };
  }).filter(Boolean);
};

export {
  buildQuestionMap,
  formatDateTime,
  resolveScheduleValue,
  getAnswerDisplay,
  getAnsweredQuestions,
};
